import SystemAgreementDisplay from '@/components/SystemAgreementDisplay';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { systemAgreement } from '@/data/systemAgreement';
import { fadeIn, staggerChildren } from '@/utils/animations';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { lazy, Suspense, useState } from 'react';
import { FaArrowRight, FaFileAlt, FaMapMarkerAlt, FaShieldAlt, FaTree, FaTruck } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';

// Lazy load components
const Header = lazy(() => import('@/components/Header'));
const Footer = lazy(() => import('@/components/Footer'));
const DENRHeader = lazy(() => import('./components/DENRHeader'));
// const ExpandableServiceCard = lazy(() => import('@/components/ui/ExpandableServiceCard'));

// Loading fallback component
const LoadingFallback = () => (
	<div className="flex items-center justify-center min-h-[200px]">
		<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
	</div>
);


// Temporary function to show system creator - To be removed when system is paid
const getSystemCreatorInfo = () => {
	return {
		show: true, // Set to false to hide the creator info
		// text: "System developed by Mark Gilbert Reginio. ",
		technicalInfo: "This system consists of 137,403 lines of code(93,724 lines after optimization) across 100+ files, developed within a span of 7 months. Originally developed for DENR-PENRO Marinduque, the system's architecture is designed to be potentially be implemented by other DENR offices in the future.",
		agreement: systemAgreement
	};
};

const ServiceCard = ({ service, index }) => {
	const Icon = service.icon;
	const navigate = useNavigate();

	const handleServiceClick = () => {
		navigate('/services', { state: { selectedService: service } });
	};

	return (
		<Card className="h-[280px] cursor-pointer transition-shadow hover:shadow-lg">
			<CardHeader className="p-6">
				<div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-4 transition-colors group-hover:bg-green-100">
					<Icon className="text-green-600 text-2xl" />
				</div>
				<h3 className="font-semibold text-gray-800 text-xl">
					{service.title}
				</h3>
			</CardHeader>
			<CardContent className="px-6">
				<p className="text-gray-600">
					{service.description}
				</p>
			</CardContent>
			<CardFooter className="px-6 mt-auto border-t border-gray-100">
				<button
					className="flex items-center text-green-600 font-medium text-sm hover:underline ml-auto"
					onClick={handleServiceClick}
				>
					View details <FaArrowRight className="ml-2" />
				</button>
			</CardFooter>
		</Card>
	);
};

const FeatureHighlight = ({ icon: Icon, title, description }) => {
	return (
		<motion.div
			className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-md border border-gray-100"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
		>
			<div className="p-4 bg-green-50 rounded-full mb-4">
				<Icon className="text-green-600 text-2xl" />
			</div>
			<h3 className="text-xl font-semibold text-gray-800 mb-2 text-center">{title}</h3>
			<p className="text-gray-600 text-center">{description}</p>
		</motion.div>
	);
};

const LandingPage = () => {
	const creatorInfo = getSystemCreatorInfo();
	const [showAgreement, setShowAgreement] = useState(false);
	const navigate = useNavigate();

	const handleServiceClick = (index) => {
		navigate('/services', { state: { selectedServiceIndex: index } });
	};

	const services = [
		{
			icon: FaTree,
			title: "Tree Cutting Permits",
			description: "Apply for permits to legally cut trees, ensuring sustainable forest management practices.",
			process: [
				"Determine if the trees are on public or private land and verify eligibility with PENRO Marinduque.",
				"Submit an online application form or letter of intent via this website.",
				"Upload required documents: proof of land ownership (title/tax declaration), tree inventory (species, diameter, location), and an environmental management plan.",
				"Pay the application fee (approx. PHP 1,000+ for public land, varies for private land, subject to change).",
				"Schedule a site inspection with PENRO Marinduque staff.",
				"Receive the permit decision within 20–40 working days (public land) or 15–30 days (private land) after inspection.",
				"If approved, download and print your tree cutting permit digitally."
			],
		},
		{
			icon: FaShieldAlt,
			title: "Chainsaw Registration",
			description: "Register your chainsaw to comply with regulations and promote responsible use of forestry equipment.",
			process: [
				"Fill out the online chainsaw registration form on the PermitTree website or obtain a form from the office.",
				"Submit proof of ownership (receipt or deed of sale) for the chainsaw.",
				"Provide chainsaw specifications and photos (serial number, model, etc.).",
				"Pay the registration fee.",
				"Schedule a chainsaw inspection at the nearest PermitTree office.",
				"Receive a digital registration certificate within 7 working days after approval."
			],
		},
		{
			icon: FaFileAlt,
			title: "Certificate of Verification (COV)",
			description: "Obtain a COV for transporting planted trees and non-timber forest products within private land.",
			process: [
				"Determine eligibility (planted trees, non-premium timber, non-timber products, or private land wood; bamboo exempt per DAO 2021-26).",
				"Submit an online application for COV via this website.",
				"Upload proof of land ownership (title/tax declaration) and plantation details/inventory of trees/products to be transported.",
				"Provide a barangay certification for non-timber products, if required.",
				"Pay any required processing fees (typically minimal, subject to change).",
				"Undergo a virtual or on-site verification process scheduled by PENRO Marinduque.",
				"Receive a digital COV within 15–30 working days after approval; present the digital or printed COV during transport."
			],
		},
		{
			icon: FaMapMarkerAlt,
			title: "Private Tree Plantation Registration (PTPR)",
			description: "Register your private tree plantations to establish ownership and comply with DENR policies.",
			process: [
				"Complete the online PTPR application form available on the PermitTree website.",
				"Upload proof of land ownership (e.g., land title, tax declaration) and a plantation inventory (species, area, age).",
				"Pay the registration fee (approx. PHP 200–500, subject to change).",
				"Schedule an on-site verification by PENRO Marinduque personnel.",
				"Receive a digital PTPR certificate within 10–20 working days after successful verification."
			],
		},
		{
			icon: FaTruck,
			title: "National Government Agency Projects",
			description: "Get permits for tree cutting or earth balling for trees affected by national government agency projects.",
			process: [
				"Confirm project eligibility with PENRO Marinduque—applies to NGA projects on public or private lands requiring tree removal or transplanting.",
				"Complete the online application form via this website.",
				"Provide a detailed environmental impact assessment (EIA), project plans, and tree inventory (species, location, condition).",
				"Propose a tree replacement or relocation plan (earth balling, if applicable).",
				"Pay the processing fee (varies by project scope, typically PHP 1,000+).",
				"Undergo review by the PENRO Marinduque technical committee and attend a virtual consultation, if required.",
				"Schedule an inspection and assessment by PENRO staff.",
				"Receive a decision within 30–60 working days, pending environmental clearance and approval.",
				"If approved, download the special tree cutting/earth balling permit digitally."
			],
		},
		{
			icon: FaTree,
			title: "Private Land Timber Permits",
			description: "Apply for PLTP for non-premium species or SPLTP for premium/naturally-grown trees on private lands.",
			process: [
				"Choose between PLTP (non-premium species) or SPLTP (premium/naturally-grown trees) based on tree species.",
				"Complete the online application form via this website.",
				"Upload proof of land ownership (title/tax declaration) and proof of tree ownership.",
				"Provide a comprehensive tree inventory (species, diameter, volume) and a harvesting plan.",
				"Submit a sustainable management plan for the area to ensure environmental compliance.",
				"Pay the application fee (non-premium: ~PHP 500–1,500; premium: higher, subject to volume and species, subject to change).",
				"Schedule and undergo field validation by PENRO Marinduque staff.",
				"Attend an online seminar on sustainable harvesting practices, if required by PENRO.",
				"Receive a permit decision within 15–30 working days after field validation.",
				"If approved, download the digital permit for immediate use."
			],
		}
	];

	const features = [
		{
			icon: FaTree,
			title: "Sustainable Forestry",
			description: "Supporting responsible forest management practices through efficient permitting processes."
		},
		{
			icon: FaShieldAlt,
			title: "Regulatory Compliance",
			description: "Ensuring all forest activities adhere to environmental regulations and standards."
		},
		{
			icon: FaTruck,
			title: "Streamlined Process",
			description: "Simplified digital workflows that reduce paperwork and processing time."
		}
	];

	return (
		<div className="flex flex-col min-h-screen bg-white">
			<Suspense fallback={<LoadingFallback />}>
				<Header />
			</Suspense>
			<Suspense fallback={<LoadingFallback />}>
				<DENRHeader />
			</Suspense>
			<motion.main className="flex-1">
				{/* Hero Section */}
				<section className="relative overflow-hidden bg-gradient-to-b from-green-50 to-white">
					{/* Background Elements */}
					<div className="absolute top-0 left-0 w-full h-full">
						<div className="absolute top-[10%] right-[5%] w-64 h-64 rounded-full bg-green-100 opacity-40 blur-3xl"></div>
						<div className="absolute bottom-[20%] left-[10%] w-72 h-72 rounded-full bg-green-200 opacity-30 blur-3xl"></div>
					</div>

					<div className="container mx-auto px-6 py-20 md:py-28 relative z-10">
						<motion.div
							className="max-w-4xl mx-auto text-center"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.8 }}
						>
							<h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-green-800 mb-6 leading-tight">
								Welcome to <span className="text-green-600">PermitTree</span>
							</h1>

							<p className="text-xl md:text-2xl text-gray-700 mb-10 max-w-3xl mx-auto">
								Streamlined permitting for tree-related activities. Protecting our environment, one permit at a time.
							</p>

							<div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6">
								<Link to="/auth" className="w-full sm:w-auto">
									<Button className="bg-green-600 hover:bg-green-700 text-white py-6 px-8 rounded-xl text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 w-full sm:w-auto">
										Get Started
									</Button>
								</Link>

								<Link to="/learnMore" className="w-full sm:w-auto">
									<Button
										variant="outline"
										className="border-2 border-green-600 text-green-600 hover:bg-green-50 py-6 px-8 rounded-xl text-lg font-medium w-full sm:w-auto"
									>
										Learn More
									</Button>
								</Link>
							</div>
						</motion.div>
					</div>
				</section>

				{/* Features Section */}
				<section className="py-16 bg-white">
					<div className="container mx-auto px-6">
						<motion.div
							className="max-w-3xl mx-auto text-center mb-16"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5 }}
						>
							<h2 className="text-3xl font-bold text-gray-800 mb-4">
								About <span className="text-green-600">PermitTree</span>
							</h2>
							<p className="text-xl text-gray-600">
								Our digital platform streamlines tree permitting while ensuring environmental protection
							</p>
						</motion.div>

						<div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
							{features.map((feature, index) => (
								<FeatureHighlight key={index} {...feature} />
							))}
						</div>
					</div>
				</section>

				{/* Services Section */}
				<section className="py-20 bg-gray-50">
					<div className="container mx-auto px-4">
						<motion.div
							className="text-center mb-12"
							variants={fadeIn}
						>
							<h2 className="text-3xl font-bold text-green-800 mb-4">Our Services</h2>
							<p className="text-lg text-gray-600 max-w-2xl mx-auto">
								Explore our range of services designed to help you manage your tree-related activities in compliance with environmental regulations.
							</p>
						</motion.div>

						<motion.div
							className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
							variants={staggerChildren}
						>
							{services.slice(0, 3).map((service, index) => (
								<Card
									key={index}
									className="h-[280px] cursor-pointer transition-shadow hover:shadow-lg"
									onClick={() => handleServiceClick(index)}
								>
									<CardHeader className="p-6">
										<div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-4">
											<service.icon className="text-green-600 text-2xl" />
										</div>
										<h3 className="text-xl font-semibold text-green-800">
											{service.title}
										</h3>
									</CardHeader>
									<CardContent className="px-6">
										<p className="text-gray-600">
											{service.description}
										</p>
									</CardContent>
									<CardFooter className="px-6 mt-auto border-t border-gray-100">
										<button
											className="flex items-center text-green-600 font-medium text-sm hover:underline ml-auto"
											onClick={(e) => {
												e.stopPropagation();
												handleServiceClick(index);
											}}
										>
											View details <ChevronDown className="ml-1 h-4 w-4" />
										</button>
									</CardFooter>
								</Card>
							))}
						</motion.div>

						<motion.div
							className="text-center mt-12"
							variants={fadeIn}
						>
							<Link
								to="/services"
								className="inline-flex items-center text-green-600 font-semibold hover:text-green-700"
							>
								View all services <ChevronRight className="ml-2 h-5 w-5" />
							</Link>
						</motion.div>
					</div>
				</section>

				{/* Call to Action Section */}
				<section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
					<div className="container mx-auto px-6">
						<motion.div
							className="max-w-4xl mx-auto text-center"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5 }}
						>
							<h2 className="text-3xl md:text-4xl font-bold mb-6">
								Access DENR-PENRO Marinduque's Online Permitting System
							</h2>
							<p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
								Apply for tree-related permits and registrations through our digital platform
							</p>

							<Link to="/auth">
								<Button className="bg-white text-green-700 hover:bg-green-100 py-6 px-10 rounded-xl text-lg font-medium shadow-lg">
									Apply for a Permit
								</Button>
							</Link>
						</motion.div>
					</div>
				</section>
			</motion.main>

			<Suspense fallback={<LoadingFallback />}>
				<Footer />
			</Suspense>

			{/* Agreement Dialog */}
			<Dialog open={showAgreement} onOpenChange={setShowAgreement}>
				<DialogContent className="max-w-5xl max-h-[80vh]">
					<DialogHeader>
						<DialogTitle>System Development and Transfer Agreement</DialogTitle>
					</DialogHeader>
					<div className="mt-4 overflow-y-auto pr-4 max-h-[70vh] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
						<SystemAgreementDisplay />
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default LandingPage;
